
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingActions from '../../components/feature/FloatingActions';
import AnimatedSection from '../../components/base/AnimatedSection';

const PortfolioPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', name: 'All Projects' },
    { id: 'dental', name: 'Dental Lab' },
    { id: 'research', name: 'Research' },
    { id: 'diagnostic', name: 'Diagnostics' },
    { id: 'partnership', name: 'Partnerships' },
  ];

  const projects = [
    {
      id: 1,
      category: 'dental',
      image: 'https://public.readdy.ai/ai/img_res/f31f8800244ebb5a5f8b6bf208746ee1.jpg',
      title: 'Complete Smile Restoration',
      client: 'Premier Dental Clinic',
      description: 'Full mouth rehabilitation with custom ceramic crowns and bridges',
    },
    {
      id: 2,
      category: 'research',
      image: 'https://readdy.ai/api/search-image?query=medical%20research%20laboratory%20scientists%20working%20with%20advanced%20equipment%20clean%20modern%20facility%20clinical%20trial%20testing%20high%20quality%20professional&width=600&height=400&seq=port2&orientation=landscape',
      title: 'Cancer Research Initiative',
      client: 'National Cancer Institute',
      description: 'Advanced molecular diagnostics for cancer biomarker research',
    },
    {
      id: 3,
      category: 'diagnostic',
      image: 'https://readdy.ai/api/search-image?query=medical%20diagnostic%20laboratory%20testing%20equipment%20modern%20healthcare%20facility%20blood%20analysis%20machines%20clean%20professional%20high%20quality&width=600&height=400&seq=port3&orientation=landscape',
      title: 'Hospital Laboratory Partnership',
      client: 'City General Hospital',
      description: 'Comprehensive diagnostic services for 500-bed hospital facility',
    },
    {
      id: 4,
      category: 'partnership',
      image: 'https://readdy.ai/api/search-image?query=medical%20professionals%20handshake%20partnership%20healthcare%20collaboration%20clean%20modern%20office%20professional%20business%20meeting%20high%20quality&width=600&height=400&seq=port4&orientation=landscape',
      title: 'Regional Healthcare Network',
      client: 'Healthcare Alliance Group',
      description: 'Multi-facility laboratory services network implementation',
    },
    {
      id: 5,
      category: 'dental',
      image: 'https://readdy.ai/api/search-image?query=dental%20implant%20prosthetics%20showcase%20on%20clean%20white%20background%20professional%20dental%20laboratory%20work%20high%20quality%20precision%20craftsmanship&width=600&height=400&seq=port5&orientation=landscape',
      title: 'Implant Restoration Program',
      client: 'Advanced Dental Center',
      description: 'Custom implant-supported restorations with digital workflow',
    },
    {
      id: 6,
      category: 'research',
      image: 'https://readdy.ai/api/search-image?query=pharmaceutical%20laboratory%20research%20testing%20clean%20modern%20facility%20drug%20development%20equipment%20professional%20scientists%20high%20quality&width=600&height=400&seq=port6&orientation=landscape',
      title: 'Pharmaceutical Development',
      client: 'BioTech Innovations',
      description: 'Quality control and validation for new drug development',
    },
    {
      id: 7,
      category: 'diagnostic',
      image: 'https://readdy.ai/api/search-image?query=medical%20laboratory%20genetic%20testing%20DNA%20analysis%20equipment%20modern%20facility%20molecular%20diagnostics%20clean%20professional%20high%20quality&width=600&height=400&seq=port7&orientation=landscape',
      title: 'Genetic Testing Program',
      client: 'Genetics Medical Center',
      description: 'Comprehensive genetic screening and counseling services',
    },
    {
      id: 8,
      category: 'partnership',
      image: 'https://readdy.ai/api/search-image?query=medical%20clinic%20laboratory%20partnership%20modern%20healthcare%20facility%20professional%20collaboration%20clean%20environment%20high%20quality&width=600&height=400&seq=port8&orientation=landscape',
      title: 'Clinic Network Integration',
      client: 'Primary Care Associates',
      description: 'Integrated laboratory services for 25 primary care clinics',
    },
    {
      id: 9,
      category: 'dental',
      image: 'https://readdy.ai/api/search-image?query=orthodontic%20appliances%20clear%20aligners%20on%20clean%20white%20background%20professional%20dental%20laboratory%20products%20high%20quality%20modern%20orthodontics&width=600&height=400&seq=port9&orientation=landscape',
      title: 'Orthodontic Solutions',
      client: 'Smile Orthodontics',
      description: 'Custom clear aligners and orthodontic appliance fabrication',
    },
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FloatingActions />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=medical%20laboratory%20success%20stories%20portfolio%20showcase%20professional%20healthcare%20achievements%20modern%20facility%20high%20quality&width=1920&height=800&seq=portfolio-hero&orientation=landscape)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-royal-900/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl lg:text-6xl font-display font-bold mb-4">Our Portfolio</h1>
          <p className="text-xl mb-6 max-w-2xl mx-auto">
            Showcasing our successful projects and partnerships
          </p>
          <div className="flex items-center justify-center space-x-2 text-white/80">
            <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>Portfolio</span>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap justify-center gap-3">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeFilter === filter.id
                    ? 'bg-primary-500 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <AnimatedSection key={project.id} delay={index * 0.1}>
                <div className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer">
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        <h3 className="text-2xl font-display font-bold mb-2">{project.title}</h3>
                        <p className="text-white/90 mb-1">{project.client}</p>
                        <p className="text-sm text-white/80 mb-4">{project.description}</p>
                        <div className="flex items-center space-x-2 text-white font-semibold">
                          <span>View Case Study</span>
                          <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 group-hover:bg-primary-50 transition-colors duration-300">
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-semibold capitalize">
                        {project.category}
                      </span>
                      <i className="ri-arrow-right-line text-primary-600 text-xl group-hover:translate-x-2 transition-transform duration-300"></i>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stats */}
      <AnimatedSection className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Our Impact in Numbers
            </h2>
            <p className="text-lg text-gray-600">
              Delivering excellence across hundreds of successful projects
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center p-8 bg-gradient-to-br from-primary-50 to-cyan-50 rounded-3xl">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-primary-500 rounded-2xl">
                <i className="ri-building-line text-white text-3xl"></i>
              </div>
              <div className="text-4xl font-bold text-primary-600 mb-2">500+</div>
              <div className="text-gray-700 font-medium">Healthcare Facilities</div>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-primary-50 to-cyan-50 rounded-3xl">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-primary-500 rounded-2xl">
                <i className="ri-test-tube-line text-white text-3xl"></i>
              </div>
              <div className="text-4xl font-bold text-primary-600 mb-2">2M+</div>
              <div className="text-gray-700 font-medium">Tests Completed</div>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-primary-50 to-cyan-50 rounded-3xl">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-primary-500 rounded-2xl">
                <i className="ri-star-line text-white text-3xl"></i>
              </div>
              <div className="text-4xl font-bold text-primary-600 mb-2">99.8%</div>
              <div className="text-gray-700 font-medium">Client Satisfaction</div>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-primary-50 to-cyan-50 rounded-3xl">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-primary-500 rounded-2xl">
                <i className="ri-award-line text-white text-3xl"></i>
              </div>
              <div className="text-4xl font-bold text-primary-600 mb-2">50+</div>
              <div className="text-gray-700 font-medium">Industry Awards</div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection className="py-24 bg-gradient-to-r from-primary-600 to-royal-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
            Start Your Success Story
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Join hundreds of satisfied clients who trust MedLab Pro for their laboratory needs
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/quote"
              className="px-10 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-cyan-50 hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              Request a Quote
            </Link>
            <Link
              to="/contact"
              className="px-10 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-300 whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </div>
  );
};

export default PortfolioPage;
